import { Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { SharedClockHandlerService } from '../../../../../core/services/features/time-tracking/shared-clock-handler.service';
import * as fromTimeBlockActions from '../../../../../core/state/time-blocks/time-block-entries/time-blocks.actions';
import { TimeBlockCrudService } from '../crud/time-block-crud.service';
import { TimeBlockStructureService } from '../time-block-structure/time-block-structure.service';
import { LocalizationService } from '../../../../../core/services/localization.service';
import { UndoRedoService } from '../../../../../core/state/undo-redo/undo-redo.service';
import {
  NotificationType,
  ShiveNotificationService,
} from '../../../../../core/services/controls/shive-notification.service';
import { UserOperation } from '../../../../../core/enums/user-operation';
import {
  TimeBlockModifiedResponse,
  TimeBlockUpdatedResponse,
} from '../../../../../shared/data-types/http-response-types';
import { TimeBlockContentType, TimeBlockType } from '../types/time-block-types';
import { TimeBlockItemBuilderService } from '../generation/time-block-item-builder.service';
import { ITimeBlockComponentItem } from '../types/time-block-component-item-types';
import { TimeBlockService } from '../services/time-block.service';
import { ParallelTimeBlockGeometryCalculatorService } from '../calculation/geometry/parallel-time-block-geometry-calculator.service';
import { TimeBlockDurationCalculationService } from '../calculation/duration/time-block-duration-calculation.service';
import { CalendarHeaderService } from '../../calendar-header/calendar-header.service';
import { CalendarService } from '../../../services/calendar.service';
import { CalendarViews } from '../../../calendar-types';
import { assert } from '../../../../../core/assert/assert';
import { takeUntil } from 'rxjs/operators';
import { TimeBlockMonthViewContainerService } from '../services/time-block-month-view-container.service';

@Injectable()
export class TimeBlockHttpCallbackService {
  public selectedContentType: TimeBlockContentType = null;

  constructor(
    private readonly actions$: Actions,
    private readonly localizationService: LocalizationService,
    private readonly historyService: UndoRedoService,
    private readonly calendarService: CalendarService,
    private readonly timeBlockMonthViewContainerService: TimeBlockMonthViewContainerService,
    private readonly calendarHeaderService: CalendarHeaderService,
    private readonly timeBlockDurationCalculationService: TimeBlockDurationCalculationService,
    private readonly timeBlockService: TimeBlockService,
    private readonly parallelTimeBlockGeometryCalculatorService: ParallelTimeBlockGeometryCalculatorService,
    private readonly timeBlockItemBuilderService: TimeBlockItemBuilderService,
    private readonly timeBlockStructureService: TimeBlockStructureService,
    private readonly shiveNotificationService: ShiveNotificationService,
    private readonly timeBlockCrudService: TimeBlockCrudService,
    private readonly sharedClockHandlerService: SharedClockHandlerService,
  ) {}

  /**
   *
   * Add, update or remove the time block after http request has finished.
   */
  public handleHTTPCallbacks(): void {
    this.actions$
      .pipe(
        ofType(
          fromTimeBlockActions.timeBlockAdded,
          fromTimeBlockActions.timeBlockUpdated,
          fromTimeBlockActions.timeBlockDeleted,
        ),
        takeUntil(this.calendarService.calendarDestroyed$),
      )
      .subscribe(({ payload, type }) => {
        // Render all altered time blocks returned from the server.
        if (
          type === fromTimeBlockActions.timeBlockAdded.type ||
          type === fromTimeBlockActions.timeBlockUpdated.type
        ) {
          const operation =
            type === fromTimeBlockActions.timeBlockAdded.type
              ? UserOperation.Create
              : UserOperation.Update;

          if (operation === UserOperation.Update) {
            // If the time block type changed (e.g. from project to absence), remove the old time block.
            this.checkIfContentTypeChanged(payload as TimeBlockUpdatedResponse);
            const timeBlockModel = payload.modifiedTimeBlockItems[0].timeBlockModel;
            // If the time block model
            if (timeBlockModel.isActive) {
              this.sharedClockHandlerService.setClockStart = timeBlockModel.start.getTime();
            }
          }
          // Build a new time block.
          this.generateModifiedTimeBlocks(payload, operation);
        } else if (type === fromTimeBlockActions.timeBlockDeleted.type) {
          const deletedTimeBlock = this.timeBlockStructureService.retrieveTimeBlockPart(
            payload.deletedTimeBlockId,
          ) as ITimeBlockComponentItem;
          this.timeBlockCrudService.removeTimeBlock(deletedTimeBlock);
        }
        // Reset content type.
        this.selectedContentType = null;

        // Updated parallel time block positioning.
        this.parallelTimeBlockGeometryCalculatorService.updateParallelTimeBlockGeometries(true);
        this.parallelTimeBlockGeometryCalculatorService.updateParallelTimeBlockGeometries(false);

        const calendarView = this.calendarService.model.calendarViewMode;
        if (calendarView === CalendarViews.DayGrid || calendarView === CalendarViews.WeekGrid) {
          this.calendarHeaderService.renderDayOrWeekViewFixLaneDayContainer();
        } else {
          // Hide vertical overflowing time blocks.
          this.timeBlockMonthViewContainerService.toggleOverflowingTimeBlocks();
        }

        // Update time block sums
        this.timeBlockDurationCalculationService.calculateTimeBlockDurationSums$.next(null);

        const message = this.localizationService.getTranslation(payload.responseCode);
        this.shiveNotificationService.show(NotificationType.Success, message);
      });

    this.actions$.pipe(ofType(fromTimeBlockActions.httpFail)).subscribe(() => {
      // this.historyService.doUndo();
    });
  }

  private generateModifiedTimeBlocks(
    timeBlockResponse: TimeBlockModifiedResponse,
    operation: UserOperation,
  ): void {
    if (operation === UserOperation.Create) {
      // Remove the temporary non-existing time block.
      const temporaryBlock = this.timeBlockStructureService.retrieveTimeBlockPart(
        null,
        TimeBlockType.NonExistingBlock,
      ) as ITimeBlockComponentItem;

      // For the absence planner, there is no non-existing temporary time block.
      if (!temporaryBlock) {
        return;
      }
      this.timeBlockCrudService.removeTimeBlock(temporaryBlock, TimeBlockType.NonExistingBlock);
    }

    const modifiedTimeBlocks = timeBlockResponse.modifiedTimeBlockItems;
    modifiedTimeBlocks.forEach((timeBlockItem) => {
      if (this.timeBlockService.isTimeBlockOutsideOfView(timeBlockItem)) {
        return;
      }

      const modifiedTimeBlock = this.timeBlockItemBuilderService.buildFromExisting(
        timeBlockItem,
        timeBlockItem.timeBlockContentType,
      );

      const existingTimeBlock = this.timeBlockStructureService.retrieveTimeBlockPart(
        modifiedTimeBlock.id,
      ) as ITimeBlockComponentItem;

      if (existingTimeBlock) {
        this.timeBlockCrudService.removeTimeBlock(existingTimeBlock);
      }

      this.timeBlockCrudService.insertTimeBlock(modifiedTimeBlock);
    });
  }

  /**
   * If the content type changed after updating a time block (e.g. from project to absence or vice versa), a new time block
   * is generated by the server. So remove the old time block.
   */
  private checkIfContentTypeChanged(response: TimeBlockUpdatedResponse): void {
    assert(
      response.modifiedTimeBlockItems.length === 1,
      'Modified time block items count is not 1.',
    );

    if (
      this.selectedContentType !== null &&
      response.modifiedTimeBlockItems[0].timeBlockContentType !== this.selectedContentType
    ) {
      this.timeBlockCrudService.removeTimeBlock(response.originalTimeBlockItem);
    }
  }
}
